// Shared status badge (platform UI/UX polish, Phase A promotion of
// pages/MyChamber/ChamberSettings/common/StatusBadge). The pill variant is
// byte-identical to the ChamberSettings original; the text variant covers the
// colored bold status TEXT the manage tables render (Members/Events/Emails
// single line, Invoices two-line). NEW consumers must import THIS copy — the
// ChamberSettings original and its StatusTone in settingsData.ts were deleted
// in the B13 polish pass (POL-026(a)); this is the sole survivor.

// Re-declared locally on purpose: common/dashboard must not import from
// src/components/pages/** (layering invariant).
export type StatusTone = "success" | "warning" | "neutral" | "danger";

const statusToneClasses: Record<StatusTone, string> = {
  success: "bg-green-50 text-green-700 border-green-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  neutral: "bg-gray-50 text-gray-700 border-gray-200",
  danger: "bg-red-50 text-red-700 border-red-200",
};

export type StatusBadgeProps = {
  label: string;
  // "pill" (default) — ChamberSettings markup; "text" — manage-table status
  variant?: "pill" | "text";
  // pill only
  tone?: StatusTone;
  // text variant: FULL class string incl. color — the label/color maps stay
  // page-owned domain logic. Members:
  // `text-sm font-bold ${statusTextClassNames[s] || "text-gray-950"}`;
  // Emails: `text-[14px] font-extrabold ${statusTextColor(s)}`.
  textClassName?: string;
  // text variant alternative: inline style color (Events statusMeta hex)
  textColor?: string;
  // text variant second line (Invoices getRowStatusCopy().secondary)
  secondaryLabel?: string;
  secondaryClassName?: string;
  // text variant: extra short lines under the label (Invoices credit-note
  // links). Each stays on one line; a long value ends with an ellipsis and
  // shows in full on hover.
  noWrapLines?: readonly string[];
};

export const StatusBadge = ({
  label,
  variant = "pill",
  tone = "neutral",
  textClassName,
  textColor,
  secondaryLabel,
  secondaryClassName = "mt-1 text-[13px] font-extrabold leading-5 text-gray-600",
  noWrapLines = [],
}: StatusBadgeProps) => {
  if (variant === "text") {
    const textStyle = textColor ? { color: textColor } : undefined;

    if (secondaryLabel || noWrapLines.length) {
      return (
        <>
          <div className={textClassName} style={textStyle}>
            {label}
          </div>
          {secondaryLabel ? (
            <div className={secondaryClassName}>{secondaryLabel}</div>
          ) : null}
          {noWrapLines.map((line) => (
            <div
              className={`${secondaryClassName} truncate`}
              key={line}
              title={line}
            >
              {line}
            </div>
          ))}
        </>
      );
    }

    return (
      <span className={textClassName} style={textStyle}>
        {label}
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${statusToneClasses[tone]}`}
    >
      {label}
    </span>
  );
};
